import { useCallback, useEffect, useRef, useState } from 'react';

import type { ICompanyPlanBenefitsResponse } from '../../interfaces/businessData';
import { CompanyBenefitsState, ICategorizedCompanyBenefits } from '../../interfaces/plans';
import Company from '../../services/api/companies/companies';

interface IUseCompanyBenefitsProps {
  companyId: string;
  companyPlan?: number;
}

const defaultData = {
  bfuChoices: 0,
  benefits: {}
};

export const useCompanyBenefits = ({ companyId, companyPlan }: IUseCompanyBenefitsProps) => {
  const [allBenefitsState, setAllBenefitsState] = useState<CompanyBenefitsState>({
    data: defaultData,
    isLoading: true,
    error: false
  });
  const latestCallId = useRef(0);

  const categorizeBenefits = (benefitsResponse: ICompanyPlanBenefitsResponse): ICategorizedCompanyBenefits => {
    const categorizedBenefits = benefitsResponse.standard.reduce((acc, benefit) => {
      const category = benefit.category ?? 'uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(benefit);
      return acc;
    }, {} as ICategorizedCompanyBenefits);
    categorizedBenefits['all'] = benefitsResponse.standard.filter((benefit) => benefit.category !== 'health');
    return categorizedBenefits;
  };

  const fetchBenefits = useCallback(
    async (company: string) => {
      const callId = ++latestCallId.current;
      try {
        if (!company || !companyPlan) {
          return;
        }
        setAllBenefitsState({ data: defaultData, isLoading: true, error: false });

        const benefits = await Company.getPlanBenefits(company, companyPlan);
        const categorizedBenefits: ICategorizedCompanyBenefits = {};

        const categorized = categorizeBenefits(benefits);
        Object.entries(categorized).forEach(([category, benefits]) => {
          categorizedBenefits[category] = [...(categorizedBenefits[category] || []), ...benefits];
        });

        const bfuChoices = benefits.bfu.availableChoices || 0;

        if (callId !== latestCallId.current) {
          return;
        }

        setAllBenefitsState({
          data: { benefits: categorizedBenefits, bfuChoices: bfuChoices },
          isLoading: false,
          error: false
        });
      } catch {
        setAllBenefitsState({ data: defaultData, isLoading: false, error: true });
        return;
      }
    },
    [companyPlan]
  );

  useEffect(() => {
    fetchBenefits(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBenefits]);

  useEffect(() => {
    setAllBenefitsState({ data: defaultData, isLoading: true, error: false });
  }, [companyId]);

  return { allBenefitsState, refetch: fetchBenefits };
};
